import React, { useEffect, useRef, useState } from 'react';

/**
 * SectionReveal — one-shot fade-up when the section scrolls into view.
 * Uses IntersectionObserver; degrades gracefully when unavailable (SSR / very
 * old browsers).
 */
export function SectionReveal({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSeen(true);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      data-reveal=""
      data-reveal-delay={delay || undefined}
      className={`${seen ? 'in-view ' : ''}${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Stars — decorative floating dots (cyan + gold + white) rendered behind
 * hero-ish sections. Purely CSS animated — no re-renders.
 */
export function Stars({ count = 10, className = '' }) {
  return (
    <div className={`aga-stars ${className}`} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => <i key={i} />)}
    </div>
  );
}
